(() => {
  const eventNames = {
    asyncApisInitialized: 'SwymAsyncApisInitialized',
    layoutInitialized: 'SwymStorefrontLayoutInitialized',
    savedForLaterFetched: 'SwymSavedForLaterFetched',
    wishlistFetched: 'SwymWishlistFetched',
  };
  const emptyItemsSelector =
    'swym-storefront-layout-as-modal swym-storefront-layout-default-wishlist ' +
    '.swym-storefront-layout-items-has-empty';
  const modalCoreSelector = 'swym-storefront-layout-as-modal';
  const wishlistContainerSelector =
    'swym-storefront-layout-as-modal swym-storefront-layout-wishlist-container';
  const sflContainerSelector =
    'swym-storefront-layout-as-modal swym-storefront-layout-sfl-container';
  const sflListSelector = 'swym-storefront-layout-as-modal swym-storefront-layout-sfl-list';
  const sflInstructionSelector =
    ':is(swym-storefront-layout-as-modal, swym-storefront-layout-as-drawer) ' +
    'swym-storefront-layout-sfl-list .swym-storefront-layout-empty-state-sfl ' +
    '.swym-storefront-layout-empty-sfl-step:last-child ' +
    '.swym-storefront-layout-empty-sfl-step-content';
  const cartRemovalPopupSelector = '.swym-sfl-popup-dialog';
  const layoutHydrationSelector =
    'swym-storefront-layout-as-modal swym-storefront-layout-tabs, ' +
    'swym-storefront-layout-as-modal swym-storefront-layout-wishlist-container';
  const relevantLayoutSelector =
    'swym-storefront-layout-as-modal, swym-storefront-layout-wishlist-container, ' +
    'swym-storefront-layout-sfl-container, swym-storefront-layout-sfl-list, ' +
    cartRemovalPopupSelector;
  const state = (window.StonylabWishlistState = window.StonylabWishlistState || {});

  let wishlistSnapshot = state.wishlistSnapshot || null;
  let layoutSnapshot = state.layoutSnapshot || null;
  let layoutInitialized = state.layoutInitialized === true;
  let wishlistFetched = state.wishlistFetched === true || Boolean(wishlistSnapshot);
  let sflSnapshot = state.sflSnapshot || null;
  let sflFetched = state.sflFetched === true;
  let asyncApisInitialized = state.asyncApisInitialized === true;
  let triggerWaitTimer = null;
  let syncQueued = false;
  let sflInitializationPromise = null;
  let sflRecoveryTimer = null;
  let sflRecoveryAttempts = 0;
  let nativeSflWatchdogTimer = null;

  const initializedSflContainers = new WeakSet();
  const hydratedLayoutCores = new WeakSet();
  const hydratedLayoutComponents = new WeakSet();
  const hydratedSflLists = new WeakSet();
  const hydratedWishlistContainers = new WeakSet();

  const getContext = () => window.SwymStorefrontLayoutContext;
  const getSflApi = () =>
    window.SwymStorefrontLayoutAPI?.SwymSFLAsyncApis ||
    state.asyncApis?.SwymStorefrontLayoutAPI?.SwymSFLAsyncApis;
  const hasSflData = () => {
    const context = getContext();
    return context?.sflData !== undefined && context.sflData !== null;
  };

  const renderEmptyState = (itemsContainer) => {
    if (itemsContainer.querySelector('[data-stonylab-wishlist-empty]')) return;

    const rootPath = window.Shopify?.routes?.root || '/';
    const browseUrl = `${rootPath.replace(/\/?$/, '/')}collections/all`;

    itemsContainer.innerHTML = `
      <div class="swym-storefront-layout-empty-container stonylab-wishlist-empty" data-stonylab-wishlist-empty>
        <div class="swym-storefront-layout-empty-wishlist-container">
          <div class="swym-storefront-layout-empty-wishlist-content">
            <div class="swym-storefront-layout-empty-state-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"/>
              </svg>
            </div>
            <h2 class="swym-storefront-layout-empty-wishlist-title">Your wishlist is empty</h2>
            <p class="swym-storefront-layout-empty-wishlist-description">
              Keep products in one place for your next project.<br>
              Click the heart icon on a product to save it here.
            </p>
            <a class="swym-storefront-layout-empty-state-cta-button" href="${browseUrl}">
              <span>Browse Products</span>
              <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"/>
              </svg>
            </a>
            <hr class="swym-storefront-layout-empty-state-divider">
            <p class="swym-storefront-layout-empty-state-notice">
              <svg viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3.5 2.5h9v13l-4.5-3-4.5 3v-13Z"/>
              </svg>
              <span>Find your saved products here when you're ready to order.</span>
            </p>
          </div>
        </div>
      </div>
    `;
  };

  const renderSflInstruction = (container) => {
    if (container.querySelector('[data-stonylab-sfl-instruction]')) return;

    const instruction = document.createElement('span');
    instruction.className = 'stonylab-sfl-instruction';
    instruction.dataset.stonylabSflInstruction = '';

    const emphasis = document.createElement('strong');
    emphasis.textContent = 'Save for Later';

    instruction.append(
      document.createTextNode('Select '),
      emphasis,
      document.createTextNode(' beside an item.')
    );
    container.replaceChildren(instruction);
  };

  const dismissCartRemovalPopup = (dialog) => {
    if (dialog.dataset.stonylabDismissalPending === 'true') return;

    const dismissButton = dialog.querySelector('.swym-sfl-popup-cancel, .swym-close-btn');
    if (!dismissButton) return;

    dialog.dataset.stonylabDismissalPending = 'true';
    dismissButton.click();
    window.setTimeout(() => delete dialog.dataset.stonylabDismissalPending, 1000);
  };

  const prepareLayoutCoreRecovery = () => {
    if (!layoutInitialized) return [];

    const pendingCores = Array.from(document.querySelectorAll(modalCoreSelector)).filter(
      (core) => typeof core.loadStrings === 'function' && !hydratedLayoutCores.has(core)
    );

    pendingCores.forEach((core) => {
      hydratedLayoutCores.add(core);
      const swat = getContext()?.swat;

      core.checkForSwymDrawerHash?.();
      core.initElement?.();
      if (swat?.evtLayer && swat?.JSEvents?.StringsLoaded) {
        swat.evtLayer.addEventListener(swat.JSEvents.StringsLoaded, () => core.loadStrings());
      }
      core.attachSwymEventListeners?.(swat);
      core.instrumentLayoutType?.();
    });

    return pendingCores;
  };

  const loadStringsForRecoveredCores = (cores) => {
    if (!cores.length) return;

    const retailerStrings = window._swat?.retailerSettings?.Strings || {};
    const stringsAlreadyLoaded =
      retailerStrings.ThemeAppStorefrontLayoutTitle ||
      retailerStrings.ThemeAppStorefrontLayoutEmptyWishlistTitle;
    if (!stringsAlreadyLoaded) return;

    cores.forEach((core) => core.loadStrings());
  };

  const replayLayoutInitialization = () => {
    if (!layoutInitialized) return;

    const recoveredCores = prepareLayoutCoreRecovery();

    const pendingComponents = Array.from(document.querySelectorAll(layoutHydrationSelector)).filter(
      (component) =>
        (typeof component.initUI === 'function' ||
          typeof component.attachSwymEventListeners === 'function') &&
        !hydratedLayoutComponents.has(component)
    );
    pendingComponents.forEach((component) => {
      hydratedLayoutComponents.add(component);
      if (typeof component._layoutInitHandler === 'function') {
        component._layoutInitHandler();
      } else if (typeof component.attachSwymEventListeners === 'function') {
        component.attachSwymEventListeners(getContext()?.swat);
      }
    });

    // Swym normally defers this until all LayoutInitialized listeners initialize their children.
    loadStringsForRecoveredCores(recoveredCores);
  };

  const replayWishlistData = () => {
    if (!wishlistFetched || !wishlistSnapshot) return;

    const pendingContainers = Array.from(document.querySelectorAll(wishlistContainerSelector)).filter(
      (container) =>
        typeof container.renderWishlistAndCollections === 'function' &&
        !hydratedWishlistContainers.has(container)
    );
    if (!pendingContainers.length) return;

    pendingContainers.forEach((container) => hydratedWishlistContainers.add(container));
    document.dispatchEvent(
      new CustomEvent(eventNames.wishlistFetched, { detail: wishlistSnapshot })
    );
  };

  const replaySflData = () => {
    if (!sflFetched && !hasSflData()) return;

    const pendingLists = Array.from(document.querySelectorAll(sflListSelector)).filter(
      (list) => typeof list.renderSFL === 'function' && !hydratedSflLists.has(list)
    );
    if (!pendingLists.length) return;

    pendingLists.forEach((list) => hydratedSflLists.add(list));
    document.dispatchEvent(
      new CustomEvent(eventNames.savedForLaterFetched, {
        detail: sflSnapshot || { sflData: getContext()?.sflData || null },
      })
    );
  };

  const scheduleSflRecovery = (container) => {
    if (hasSflData() || sflRecoveryTimer || sflRecoveryAttempts >= 3) return;

    const delays = [500, 1500, 3000];
    const delay = delays[sflRecoveryAttempts] || delays[delays.length - 1];
    sflRecoveryTimer = window.setTimeout(() => {
      sflRecoveryTimer = null;
      initializeSfl(container, true);
    }, delay);
  };

  const initializeSfl = (container, isRetry = false) => {
    const sflApi = getSflApi();
    const canUseContainer = typeof container?.initSfl === 'function';
    const canUseApi = typeof sflApi?.updateSflList === 'function';
    if ((!canUseContainer && !canUseApi) || sflInitializationPromise) return;
    if (!isRetry && initializedSflContainers.has(container)) return;

    if (container) initializedSflContainers.add(container);
    if (isRetry) sflRecoveryAttempts += 1;

    try {
      const result = canUseApi ? sflApi.updateSflList() : container.initSfl();
      sflInitializationPromise = Promise.resolve(result)
        .catch((error) => {
          window._swat?.utils?.warn?.('Unable to initialize Saved for Later', error);
        })
        .finally(() => {
          sflInitializationPromise = null;
          queueSync();
          if (!hasSflData()) scheduleSflRecovery(container);
        });
    } catch (error) {
      sflInitializationPromise = null;
      window._swat?.utils?.warn?.('Unable to initialize Saved for Later', error);
      scheduleSflRecovery(container);
    }
  };

  const watchNativeSflInitialization = () => {
    if (hasSflData() || nativeSflWatchdogTimer) return;

    nativeSflWatchdogTimer = window.setTimeout(() => {
      nativeSflWatchdogTimer = null;
      if (!hasSflData()) initializeSfl(document.querySelector(sflContainerSelector), true);
    }, 5000);
  };

  const synchronizeLifecycle = () => {
    asyncApisInitialized = asyncApisInitialized || typeof getSflApi()?.updateSflList === 'function';
    replayLayoutInitialization();
    replayWishlistData();
    replaySflData();

    if (!asyncApisInitialized || hasSflData()) return;
    document.querySelectorAll(sflContainerSelector).forEach((container) => {
      if (typeof container.initSfl === 'function' && !initializedSflContainers.has(container)) {
        initializeSfl(container);
      }
    });
  };

  const synchronize = () => {
    synchronizeLifecycle();
    document.querySelectorAll(emptyItemsSelector).forEach(renderEmptyState);
    document.querySelectorAll(sflInstructionSelector).forEach(renderSflInstruction);
    document.querySelectorAll(cartRemovalPopupSelector).forEach(dismissCartRemovalPopup);
  };

  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    queueMicrotask(() => {
      syncQueued = false;
      synchronize();
    });
  }

  const isRelevantMutation = (mutation) => {
    if (mutation.target.closest?.(relevantLayoutSelector)) return true;

    return Array.from(mutation.addedNodes).some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.(relevantLayoutSelector) || node.querySelector?.(relevantLayoutSelector))
    );
  };

  const observer = new MutationObserver((mutations) => {
    if (mutations.some(isRelevantMutation)) queueSync();
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  document.addEventListener(eventNames.wishlistFetched, (event) => {
    wishlistSnapshot = event.detail || null;
    wishlistFetched = true;
    state.wishlistSnapshot = wishlistSnapshot;
    state.wishlistFetched = true;
    document.querySelectorAll(wishlistContainerSelector).forEach((container) => {
      if (typeof container.renderWishlistAndCollections === 'function') {
        hydratedWishlistContainers.add(container);
      }
    });
    queueSync();
  });

  document.addEventListener(eventNames.layoutInitialized, (event) => {
    layoutSnapshot = event.detail || null;
    layoutInitialized = true;
    state.layoutSnapshot = layoutSnapshot;
    state.layoutInitialized = true;
    document.querySelectorAll(modalCoreSelector).forEach((core) => {
      if (typeof core.loadStrings === 'function') hydratedLayoutCores.add(core);
    });
    document.querySelectorAll(layoutHydrationSelector).forEach((component) => {
      if (
        typeof component.initUI === 'function' ||
        typeof component.attachSwymEventListeners === 'function'
      ) {
        hydratedLayoutComponents.add(component);
      }
    });
    queueSync();
  });

  document.addEventListener(eventNames.savedForLaterFetched, (event) => {
    sflSnapshot = event.detail || null;
    sflFetched = true;
    state.sflSnapshot = sflSnapshot;
    state.sflFetched = true;
    document.querySelectorAll(sflListSelector).forEach((list) => {
      if (typeof list.renderSFL === 'function') hydratedSflLists.add(list);
    });
    if (hasSflData() && sflRecoveryTimer) {
      window.clearTimeout(sflRecoveryTimer);
      sflRecoveryTimer = null;
    } else if (!hasSflData()) {
      scheduleSflRecovery(document.querySelector(sflContainerSelector));
    }
    queueSync();
  });

  document.addEventListener(eventNames.asyncApisInitialized, (event) => {
    asyncApisInitialized = true;
    state.asyncApis = event.detail || null;
    state.asyncApisInitialized = true;
    let nativeInitializationStarted = false;
    document.querySelectorAll(sflContainerSelector).forEach((container) => {
      if (typeof container.initSfl === 'function') {
        initializedSflContainers.add(container);
        nativeInitializationStarted = true;
      }
    });
    state.sflInitializationStarted = nativeInitializationStarted;
    if (nativeInitializationStarted) watchNativeSflInitialization();
    queueSync();
  });

  document.addEventListener('SwymStorefrontLayoutOpened', queueSync);

  window.StonylabWishlist = window.StonylabWishlist || {};
  window.StonylabWishlist.open = () => {
    const openNativeWishlist = () => {
      const wishlistTitle = document.querySelector('#notepad-anchor-title');
      const wishlistTrigger =
        wishlistTitle?.closest('button, a, [role="button"]') ||
        wishlistTitle ||
        document.querySelector('button.st-wishlist-details-button');

      if (!wishlistTrigger) return false;
      wishlistTrigger.click();
      window.requestAnimationFrame(queueSync);
      return true;
    };

    if (openNativeWishlist()) return;
    if (triggerWaitTimer) return;

    let attempts = 0;
    triggerWaitTimer = window.setInterval(() => {
      attempts += 1;
      if (openNativeWishlist() || attempts >= 40) {
        window.clearInterval(triggerWaitTimer);
        triggerWaitTimer = null;
      }
    }, 100);
  };

  if (state.sflInitializationStarted) {
    document.querySelectorAll(sflContainerSelector).forEach((container) => {
      if (typeof container.initSfl === 'function') initializedSflContainers.add(container);
    });
    watchNativeSflInitialization();
  }

  if (state.layoutInitializationStarted) {
    document.querySelectorAll(layoutHydrationSelector).forEach((component) => {
      if (
        typeof component.initUI === 'function' ||
        typeof component.attachSwymEventListeners === 'function'
      ) {
        hydratedLayoutComponents.add(component);
      }
    });
  }

  if (state.layoutCoreInitializationStarted) {
    document.querySelectorAll(modalCoreSelector).forEach((core) => {
      if (typeof core.loadStrings === 'function') hydratedLayoutCores.add(core);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', synchronize, { once: true });
  } else {
    synchronize();
  }
})();
